// useAvaliacoes.js — Avaliações ("Opiniões") de um livro.
//
// Endpoints (criados em 18/08/2026, AvaliacaoController no backend):
//   GET  {API}ebook/{id}/avaliacoes  → { avaliacoes: [...], total, media, editoriais }  (público)
//   POST {API}ebook/{id}/avaliacoes  → exige Bearer token; o autor sai do token
//
// Item: { id, usuario_nome, nota (1-5), comentario, compra_verificada, created_at }
//
// Desde 19/08/2026 o POST recusa com 403 quem não comprou o livro, e a primeira
// publicação credita Insigne Coins. Quem decide se o formulário aparece é o
// `usePodeAvaliar` — este hook trata o 403 apenas como rede de segurança, para
// o caso de a compra mudar entre a checagem e o envio.
//
// A leitura continua tolerante a falha (rede fora, deploy no meio) para a página
// de produto nunca quebrar por causa da seção de opiniões.
import { useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_API;

// Mesmo esquema de chave usado no resto do site (AuthContext/checkout)
const pegarToken = () =>
  localStorage.getItem("token" + (process.env.REACT_APP_USER_FUNCTION || ""));

const calcular = (lista) => {
  const distribuicao = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  let soma = 0;
  lista.forEach((a) => {
    const n = Math.round(Number(a.nota) || 0);
    if (n >= 1 && n <= 5) { distribuicao[n] += 1; soma += n; }
  });
  const media = lista.length ? soma / lista.length : 0;
  return { media, distribuicao };
};

const useAvaliacoes = (ebookId) => {
  const [avaliacoes, setAvaliacoes] = useState([]);
  // Avaliação Editorial: o que a editora diz do livro enquanto nenhum leitor
  // avaliou. O servidor já devolve vazio quando há avaliação real, quando o
  // livro foi desligado ou quando o recurso inteiro está desligado no painel.
  const [editoriais, setEditoriais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  // "Eu escrevi este livro?" — quem responde é o servidor, na mesma chamada.
  const [podeResponder, setPodeResponder] = useState(false);
  const [respondendo, setRespondendo] = useState(false);

  const carregar = useCallback(async () => {
    if (!ebookId) return;
    setLoading(true);
    try {
      // A ROTA É PÚBLICA, MAS O TOKEN VAI JUNTO QUANDO EXISTE.
      //
      // Sem ele o servidor não tem como saber quem está lendo, e portanto não
      // tem como dizer se essa pessoa é a autora do livro. Mandar o token numa
      // rota pública não muda o que ela devolve para os outros campos — só
      // acrescenta `pode_responder`.
      const token = pegarToken();
      const resp = await fetch(`${API_URL}ebook/${ebookId}/avaliacoes`, {
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (!resp.ok) throw new Error("sem avaliacoes");
      const data = await resp.json();
      const lista = Array.isArray(data) ? data : data?.avaliacoes || [];
      setAvaliacoes(lista);
      setEditoriais(lista.length ? [] : (Array.isArray(data?.editoriais) ? data.editoriais : []));
      setPodeResponder(Boolean(data?.pode_responder));
    } catch {
      setEditoriais([]);
      setAvaliacoes([]); // rede fora → estado vazio, a página segue de pé
      setPodeResponder(false);
    } finally {
      setLoading(false);
    }
  }, [ebookId]);

  useEffect(() => { carregar(); }, [carregar]);

  // Envia uma avaliação. Lança Error com a mensagem do backend (a UI mostra).
  // `origem`: 'convite' ou 'lembrete' quando a pessoa veio pelo link do
  // e-mail. O servidor grava na primeira avaliação, e é ela que mede se o
  // convite funciona (ver ConvidarAvaliacoes).
  const enviar = useCallback(async ({ nota, comentario, origem }) => {
    setEnviando(true);
    try {
      const token = pegarToken();
      if (!token) throw new Error("Entre na sua conta para avaliar.");

      const resp = await fetch(`${API_URL}ebook/${ebookId}/avaliacoes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(origem ? { nota, comentario, origem } : { nota, comentario }),
      });

      if (!resp.ok) {
        let msg = "Não foi possível enviar sua avaliação.";
        if (resp.status === 401) msg = "Sua sessão expirou. Entre novamente para avaliar.";
        else if (resp.status === 403) msg = "Só quem comprou este livro pode avaliá-lo.";
        else if (resp.status === 429) msg = "Muitas tentativas. Aguarde um minuto e tente de novo.";
        else {
          try {
            const erro = await resp.json();
            msg = erro?.message || erro?.error || msg;
          } catch { /* resposta sem JSON → mensagem padrão */ }
        }
        throw new Error(msg);
      }

      // O backend responde quantos coins creditou (0 quando foi só edição).
      // A tela precisa disso para não prometer moeda que não veio.
      let resultado = { coins_creditados: 0 };
      try {
        resultado = await resp.json();
      } catch { /* sem corpo → segue com o padrão */ }

      await carregar();
      return resultado;
    } finally {
      setEnviando(false);
    }
  }, [ebookId, carregar]);

  /** O autor do livro responde a um comentário. */
  const responder = useCallback(async (avaliacaoId, texto) => {
    setRespondendo(true);
    try {
      const token = pegarToken();
      if (!token) throw new Error("Entre na sua conta para responder.");

      const resp = await fetch(`${API_URL}avaliacoes/${avaliacaoId}/responder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ texto }),
      });

      if (!resp.ok) {
        let msg = "Não foi possível publicar a resposta.";
        if (resp.status === 401) msg = "Sua sessão expirou. Entre novamente.";
        else if (resp.status === 403) msg = "Somente o autor deste livro pode responder aos comentários.";
        else if (resp.status === 429) msg = "Muitas respostas seguidas. Aguarde um minuto.";
        else {
          try {
            const erro = await resp.json();
            msg = erro?.message || erro?.error || msg;
          } catch { /* sem JSON → mensagem padrão */ }
        }
        throw new Error(msg);
      }

      await carregar();
    } finally {
      setRespondendo(false);
    }
  }, [carregar]);

  /** Apaga a própria resposta. */
  const removerResposta = useCallback(async (respostaId) => {
    const token = pegarToken();
    if (!token) return;
    await fetch(`${API_URL}avaliacoes/respostas/${respostaId}`, {
      method: "DELETE",
      headers: { Accept: "application/json", Authorization: "Bearer " + token },
    });
    await carregar();
  }, [carregar]);

  const { media, distribuicao } = calcular(avaliacoes);
  return {
    avaliacoes, editoriais, media, distribuicao, total: avaliacoes.length,
    loading, enviando, enviar,
    podeResponder, respondendo, responder, removerResposta,
  };
};

export default useAvaliacoes;
